import React from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { reduxForm, FieldArray, formPropTypes, SubmissionError } from 'redux-form';
import {
  Table,
  Button,
  ButtonToolbar,
  Tooltip,
  OverlayTrigger,
} from 'react-bootstrap';

import { formProps } from '../../../propTypes';
import { getGraphQLErrorAsString } from '../../../utils/graphQLErrorHandlers';
import UserSummaryBonusField from './UserSummaryBonusField';
import { setUserMonthlyBonus, setStateAllUserMonthBonuses, setMassUserMonthBonusUpdater } from '../actions';
import { getMonthBonusState, getDepartment, getDistrict, getBonusMonth } from '../selectors';
import { APPROVED, FINALIZED } from '../constants';

import ConfirmFinalizeModal, { confirmModalPropTypes, withConfirmModalState } from '../components/ConfirmFinalizeModal';
import ConfirmZeroOutModal, { confirmZeroOutModalPropTypes, withConfirmZeroOutModalState } from '../components/ConfrimZeroOutModal';


const propTypes = exact({
  validate: PropTypes.func.isRequired,
  setUserMonthlyBonusAction: PropTypes.func.isRequired,
  setStateAllUserMonthBonusesAction: PropTypes.func.isRequired,
  setMassUserMonthBonusUpdaterAction: PropTypes.func.isRequired,

  monthBonusState: PropTypes.string.isRequired,
  district: PropTypes.object,
  department: PropTypes.object,
  bonusMonth: PropTypes.string.isRequired,
  canFinalize: PropTypes.bool,

  ...formPropTypes,
  ...formProps,
  ...confirmModalPropTypes,
  ...confirmZeroOutModalPropTypes,
});

export const FORM_NAME = 'bonusSummaryForm';


const tooltip = (
  <Tooltip id="tooltip">
    <strong>Bonuses still left to be Approved for this District and Department Pairing</strong>
  </Tooltip>
);

const MaybeOverlayTrigger = (props) => {
  const { canfinalize } = props;

  const showOverlay = (canfinalize === 'true');
  if (!showOverlay) {
    return (
      <OverlayTrigger placement="top" overlay={tooltip}>
        {props.children}
      </OverlayTrigger>
    );
  }

  return (
    <React.Fragment>
      {props.children}
    </React.Fragment>
  );
};


const validate = (values) => {
  const errors = { members: [], _error: undefined };

  values.members.forEach((member) => {
    const memberErrors = {};

    const {
      approved_bonus_amount: approvedBonusAmount,
      final_bonus_amount: finalBonusAmount,
      final_notes: finalNotes,
    } = member;

    const noNotes = (!finalNotes || finalNotes.length === 0);

    const isNotMax = (finalBonusAmount < approvedBonusAmount);
    const isOverMax = (finalBonusAmount > approvedBonusAmount);
    const isNan = Number.isNaN(finalBonusAmount);
    /* NOTE: Added error message to approved_bonus_amount field, because error wouldn't display *
    *  unless field is touched but the error message is set on the notes field */
    if ((isNotMax || isOverMax) && noNotes) {
      memberErrors.final_bonus_amount = 'Needs Reason';
    }

    if (isNan) {
      memberErrors.final_bonus_amount = 'Enter in a value';
    }

    errors.members.push(memberErrors);
  });

  return errors;
};

const renderMembers = ({ fields }) => {
  return (
    fields.map((memberPath) => {
      return (
        <UserSummaryBonusField
          memberPath={memberPath}
          key={memberPath}
        />
      );
    })
  );
};

const SummaryBonusForm = (props) => {
  const {
    handleSubmit,
    setUserMonthlyBonusAction,
    setStateAllUserMonthBonusesAction,
    setMassUserMonthBonusUpdaterAction,
    error,
    canFinalize,
  } = props;

  const {
    invalid, submitting, monthBonusState,
  } = props;

  const {
    showConfirmModal, closeConfirmModal, openConfirmModal,
  } = props;

  const {
    closeConfirmZeroOutModal, openConfirmZeroOutModal, showConfirmZeroOutModal,
  } = props;

  const { district, department } = props;

  const onSubmit = async (values) => {
    const copyOfValues = Object.assign({}, values);
    const { members } = copyOfValues;

    const submitAllUserFinalBonusesArray = members.map((item) => {
      /* Return month, user_id, final_bonus_amount */
      const userFinalBonusesObject = {};

      userFinalBonusesObject.id = item.id;
      userFinalBonusesObject.final_bonus_amount = item.final_bonus_amount;
      userFinalBonusesObject.final_notes = item.final_notes;
      return (userFinalBonusesObject);
    });

    try {
      await setUserMonthlyBonusAction(submitAllUserFinalBonusesArray);
      const submitUserMonthBonusIds = members.map((item) => {
        return item.id;
      });

      await setStateAllUserMonthBonusesAction(submitUserMonthBonusIds, FINALIZED);
    } catch (e) {
      throw new SubmissionError({ _error: getGraphQLErrorAsString(e.graphQLErrors) });
    }
  };


  const submitMutations = handleSubmit(onSubmit);

  const handleConfirmFinalize = () => {
    submitMutations();
    closeConfirmModal();
  };

  const onZeroOutSubmit = async () => {
    try {
      await setMassUserMonthBonusUpdaterAction(0, 'did not meet financials');
    } catch (e) {
      throw new SubmissionError({ _error: getGraphQLErrorAsString(e.graphQLErrors) });
    }
  };

  const handleZeroOutConfirm = () => {
    onZeroOutSubmit();
    closeConfirmZeroOutModal();
  };

  const isApproved = (monthBonusState === APPROVED);
  const areDistrictDepartmentSelected = (district && department);

  return (
    <div>
      <form style={{ paddingBottom: '20px' }}>
        <MaybeOverlayTrigger canfinalize={`${canFinalize}`}>
          <div>
            {isApproved && areDistrictDepartmentSelected
            && (
            <ButtonToolbar style={{ padding: '10px' }} className="pull-right">
              <Button
                bsStyle="primary"
                onClick={() => { return openConfirmModal(); }}
                disabled={(submitting || invalid || !canFinalize)}
              >
                Finalize
              </Button>
              <Button
                bsStyle="warning"
                onClick={() => { return openConfirmZeroOutModal(); }}
                disabled={(submitting || invalid || !canFinalize)}
              >
                Zero Out
              </Button>
            </ButtonToolbar>
            )}
          </div>
        </MaybeOverlayTrigger>
        {error
        && <span className="form-error"><strong>{error}</strong></span>}
      </form>
      {isApproved
      && (
      <div style={{ paddingBottom: '20px' }}>
        <span className="alert-warning">
          Note: Filter by district and department in order to finalize.
        </span>
      </div>
      )}
      <Table condensed>
        <thead>
          <tr>
            <th>Employee</th>
            <th>District</th>
            <th>Department</th>
            <th>Bonus Oppurtunity</th>
            <th>Total Per Diem Amount </th>
            <th>Recommended Bonus Amount</th>
            <th>Manager Notes </th>
            <th>Final Bonus</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          <FieldArray
            name="members"
            component={renderMembers}
          />
        </tbody>
      </Table>

      <ConfirmFinalizeModal
        show={showConfirmModal}
        onHide={closeConfirmModal}
        confirmFinalize={handleConfirmFinalize}
      />
      <ConfirmZeroOutModal
        show={showConfirmZeroOutModal}
        onHide={closeConfirmZeroOutModal}
        confirmFinalize={handleZeroOutConfirm}
      />
    </div>
  );
};


SummaryBonusForm.propTypes = propTypes;

/* eslint-disable react/forbid-prop-types, react/require-default-props */
renderMembers.propTypes = exact({
  fields: PropTypes.object.isRequired,
  meta: PropTypes.object,
});

/* eslint-disable react/forbid-prop-types */
MaybeOverlayTrigger.propTypes = exact({
  canfinalize: PropTypes.string.isRequired,
  children: PropTypes.object.isRequired,
});
/* eslint-enable react/forbid-prop-types */
/* eslint-enable react/forbid-prop-types, react/require-default-props */

const withForm = reduxForm({
  form: FORM_NAME,
  validate,
});

const mapStateToProps = (state) => {
  return {
    monthBonusState: getMonthBonusState(state),
    district: getDistrict(state),
    department: getDepartment(state),
    bonusMonth: getBonusMonth(state),
  };
};

const mapDispatchToProps = {
  setUserMonthlyBonusAction: setUserMonthlyBonus,
  setStateAllUserMonthBonusesAction: setStateAllUserMonthBonuses,
  setMassUserMonthBonusUpdaterAction: setMassUserMonthBonusUpdater,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

const enhance = compose(
  withForm,
  withRedux,
  withConfirmModalState,
  withConfirmZeroOutModalState,
);

export default enhance(SummaryBonusForm);
